import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Area, AreaChart
} from 'recharts';
import {
  Brain, MessageCircle, FileText, TrendingUp, AlertTriangle,
  Target, GitCompare, Lightbulb, Send, ThumbsUp, ThumbsDown,
  RefreshCw, Check, X, Loader,
  Zap, HelpCircle, BarChart3
} from 'lucide-react';
import api from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './AIAssistant.css';

const TAB_ICONS = {
  chat: MessageCircle,
  summary: FileText,
  trends: TrendingUp,
  anomalies: AlertTriangle,
  forecast: Target,
  compare: GitCompare,
  recommendations: Lightbulb,
};

const TAB_KEYS = ['chat', 'summary', 'trends', 'anomalies', 'forecast', 'compare', 'recommendations'];

const PRIORITY_COLORS = {
  critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#94a3b8',
};

function AIAssistant() {
  const { t } = useTranslation(['ai', 'common']);

  const TABS = TAB_KEYS.map(key => ({
    key,
    label: t(`ai:tabs.${key}`),
    icon: TAB_ICONS[key],
  }));

  const PRIORITY_LABELS = {
    critical: t('common:priorities.critical'),
    high: t('common:priorities.high'),
    medium: t('common:priorities.medium'),
    low: t('common:priorities.low'),
  };

  const STATUS_LABELS = {
    new: t('ai:recommendations.new'),
    viewed: t('ai:recommendations.viewed'),
    accepted: t('ai:recommendations.accepted'),
    rejected: t('ai:recommendations.rejected'),
    implemented: t('ai:recommendations.implemented'),
  };

  const [activeTab, setActiveTab] = useState('chat');
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);

  // Данные вкладок
  const [summaryData, setSummaryData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [anomaliesData, setAnomaliesData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  // Чат
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const messagesEndRef = useRef(null);

  // Сравнение
  const [compareDept1, setCompareDept1] = useState('');
  const [compareDept2, setCompareDept2] = useState('');

  // Загрузка справочников
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await api.getDepartments();
        setDepartments(res.data?.results || res.data || []);
      } catch { /* ignore */ }
    };
    loadDepartments();
  }, []);

  // Загрузка подсказок
  useEffect(() => {
    const loadSuggested = async () => {
      try {
        const res = await api.aiSuggestedQuestions();
        setSuggestedQuestions(res.data?.questions || []);
      } catch { /* ignore */ }
    };
    loadSuggested();
  }, []);

  // Автопрокрутка чата
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Загрузка данных вкладки
  const loadTabData = useCallback(async (tab) => {
    if (tab === 'chat') return;
    setLoading(true);
    try {
      const params = { year };
      if (departmentId) params.department_id = departmentId;

      switch (tab) {
        case 'summary': {
          const res = await api.aiGetSummary(params);
          setSummaryData(res.data);
          break;
        }
        case 'trends': {
          const res = await api.aiGetTrends(params);
          setTrendsData(res.data);
          break;
        }
        case 'anomalies': {
          const res = await api.aiGetAnomalies(params);
          setAnomaliesData(res.data);
          break;
        }
        case 'forecast': {
          const res = await api.aiGetForecast(params);
          setForecastData(res.data);
          break;
        }
        case 'compare': {
          const res = await api.aiCompare({ year });
          setCompareData(res.data);
          break;
        }
        case 'recommendations': {
          const res = await api.aiGetRecommendations({ ordering: '-created_at' });
          setRecommendations(res.data?.results || res.data || []);
          break;
        }
        default: break;
      }
    } catch (error) {
      console.error(`Ошибка загрузки ${tab}:`, error);
    }
    setLoading(false);
  }, [year, departmentId]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  // ─── Чат ──────────────────────────────────────────────────────────────

  const sendMessage = async (text) => {
    const question = text || inputText.trim();
    if (!question) return;

    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInputText('');
    setChatLoading(true);

    try {
      const res = await api.aiAsk({ question, year });
      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.answer || t('ai:chat.noAnswer'),
        data: data.data,
        chartType: data.chart_type,
        confidence: data.confidence,
        relatedQuestions: data.related_questions,
        conversationId: data.conversation_id,
        feedback: null,
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: t('ai:chat.errorMessage'),
        error: true,
      }]);
    }
    setChatLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendFeedback = async (msgIndex, isHelpful) => {
    const msg = messages[msgIndex];
    if (!msg.conversationId) return;
    try {
      await api.aiSendFeedback(msg.conversationId, { is_helpful: isHelpful });
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, feedback: isHelpful } : m
      ));
    } catch { /* ignore */ }
  };

  const renderConfidence = (confidence) => {
    if (confidence == null) return null;
    const pct = Math.round(confidence * 100);
    const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
    return (
      <span className="confidence-badge" style={{ color }}>
        <Zap size={12} /> {t('ai:chat.confidence', { pct })}
      </span>
    );
  };

  const renderChatChart = (data, chartType) => {
    if (!data || !chartType) return null;
    if (chartType === 'bar' && Array.isArray(data)) {
      return (
        <div className="chat-chart">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
    if (chartType === 'ranking' && Array.isArray(data)) {
      return (
        <div className="chat-ranking">
          {data.slice(0, 5).map((item, i) => (
            <div key={i} className="ranking-item">
              <span className={`rank-num ${i < 3 ? 'top' : ''}`}>{i + 1}</span>
              <span className="rank-name">{item.department_name || item.name}</span>
              <span className="rank-score">{item.kpi_score || item.value || ''}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChat = () => (
    <div className="chat-container">
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <Brain size={48} className="welcome-icon" />
            <h3>{t('ai:chat.welcome')}</h3>
            <p>{t('ai:chat.welcomeHint')}</p>
            {suggestedQuestions.length > 0 && (
              <div className="quick-actions">
                {suggestedQuestions.slice(0, 6).map((q, i) => (
                  <button key={i} className="quick-btn" onClick={() => sendMessage(q)}>
                    <HelpCircle size={14} /> {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`chat-msg ${msg.role}`}>
            <div className="msg-bubble">
              <div className="msg-text">{msg.text}</div>
              {msg.role === 'assistant' && renderChatChart(msg.data, msg.chartType)}
              {msg.role === 'assistant' && !msg.error && (
                <div className="msg-meta">
                  {renderConfidence(msg.confidence)}
                  <div className="msg-feedback">
                    <button
                      className={`fb-btn ${msg.feedback === true ? 'active' : ''}`}
                      onClick={() => sendFeedback(i, true)}
                      title={t('ai:chat.helpful')}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      className={`fb-btn ${msg.feedback === false ? 'active' : ''}`}
                      onClick={() => sendFeedback(i, false)}
                      title={t('ai:chat.notHelpful')}
                    >
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                </div>
              )}
              {msg.relatedQuestions?.length > 0 && (
                <div className="related-questions">
                  {msg.relatedQuestions.map((q, j) => (
                    <button key={j} className="related-btn" onClick={() => sendMessage(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {chatLoading && (
          <div className="chat-msg assistant">
            <div className="msg-bubble typing">
              <Loader size={16} className="spinner" /> {t('ai:chat.thinking')}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('ai:chat.placeholder')}
          rows={1}
          disabled={chatLoading}
        />
        <button
          className="send-btn"
          onClick={() => sendMessage()}
          disabled={chatLoading || !inputText.trim()}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );

  // ─── Сводка ───────────────────────────────────────────────────────────

  const generateSummary = async () => {
    setLoading(true);
    try {
      const data = { year };
      if (departmentId) data.department_id = Number(departmentId);
      const res = await api.aiGenerateSummary(data);
      setSummaryData(res.data);
    } catch (error) {
      console.error('Ошибка генерации сводки:', error);
    }
    setLoading(false);
  };

  const renderSummary = () => {
    const keyMetrics = summaryData?.key_metrics || summaryData?.result_data || {};
    const summaryText = summaryData?.summary_text || summaryData?.result_text || '';

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('ai:summary.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label>{t('ai:summary.department')}:</label>
          <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
            <option value="">{t('ai:summary.allOrganization')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <button className="action-btn" onClick={generateSummary} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} /> {t('ai:summary.generate')}
          </button>
        </div>

        {summaryText && (
          <div className="section-card summary-card">
            <h3><FileText size={18} /> {t('ai:summary.title')}</h3>
            <div className="summary-text">{summaryText}</div>
          </div>
        )}

        {Object.keys(keyMetrics).length > 0 && (
          <div className="stats-grid">
            {keyMetrics.total_reports != null && (
              <div className="stat-card blue">
                <FileText size={24} />
                <div>
                  <div className="stat-value">{keyMetrics.total_reports}</div>
                  <div className="stat-label">{t('ai:summary.reports')} ({t('ai:summary.reportsApproved', { rate: keyMetrics.approval_rate || 0 })})</div>
                </div>
              </div>
            )}
            {keyMetrics.total_documents != null && (
              <div className="stat-card orange">
                <Target size={24} />
                <div>
                  <div className="stat-value">{keyMetrics.total_documents}</div>
                  <div className="stat-label">{t('ai:summary.documents')}</div>
                </div>
              </div>
            )}
            {keyMetrics.total_plans != null && (
              <div className="stat-card green">
                <BarChart3 size={24} />
                <div>
                  <div className="stat-value">{keyMetrics.total_plans}</div>
                  <div className="stat-label">{t('ai:summary.plans')} ({t('ai:summary.plansCompletion', { rate: keyMetrics.avg_plan_completion || 0 })})</div>
                </div>
              </div>
            )}
          </div>
        )}

        {!summaryText && !loading && (
          <div className="no-data">
            {t('ai:summary.noData')}
          </div>
        )}
      </div>
    );
  };

  // ─── Тренды ───────────────────────────────────────────────────────────

  const generateTrends = async () => {
    setLoading(true);
    try {
      const data = { year };
      if (departmentId) data.department_id = Number(departmentId);
      const res = await api.aiGenerateTrends(data);
      setTrendsData(res.data);
    } catch (error) {
      console.error('Ошибка анализа трендов:', error);
    }
    setLoading(false);
  };

  const renderTrends = () => {
    const metricTrends = trendsData?.metric_trends || trendsData?.result_data?.metric_trends || [];
    const summaryText = trendsData?.summary_text || trendsData?.result_text || '';

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('ai:summary.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label>{t('ai:summary.department')}:</label>
          <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
            <option value="">{t('ai:summary.allOrganization')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <button className="action-btn" onClick={generateTrends} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} /> {t('ai:trends.analyze')}
          </button>
        </div>

        {summaryText && (
          <div className="section-card">
            <h3><TrendingUp size={18} /> {t('ai:trends.title')}</h3>
            <div className="summary-text">{summaryText}</div>
          </div>
        )}

        {metricTrends.length > 0 && (
          <>
            <div className="chart-card">
              <h3><TrendingUp size={18} /> {t('ai:trends.detectedTrends')}</h3>
              <div className="trends-list">
                {metricTrends.map((trend, i) => (
                  <div key={i} className="trend-item">
                    <span className={`trend-direction ${trend.direction}`}>
                      {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
                    </span>
                    <div className="trend-info">
                      <strong>{trend.metric_name || trend.metric}</strong>
                      <span className="trend-detail">
                        R² = {(trend.r_squared || 0).toFixed(2)} | {t('ai:trends.slope')}: {(trend.slope || 0).toFixed(3)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {metricTrends.some(t => t.values?.length > 0) && (
              <div className="chart-card">
                <h3>{t('ai:trends.metricDynamics')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={metricTrends[0]?.values || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#7c3aed" strokeWidth={2} name={t('ai:trends.value')} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}

        {!summaryText && metricTrends.length === 0 && !loading && (
          <div className="no-data">{t('ai:trends.noData')}</div>
        )}
      </div>
    );
  };

  // ─── Аномалии ─────────────────────────────────────────────────────────

  const generateAnomalies = async () => {
    setLoading(true);
    try {
      const data = { year };
      if (departmentId) data.department_id = Number(departmentId);
      const res = await api.aiGenerateAnomalies(data);
      setAnomaliesData(res.data);
    } catch (error) {
      console.error('Ошибка поиска аномалий:', error);
    }
    setLoading(false);
  };

  const renderAnomalies = () => {
    const anomalies = anomaliesData?.anomalies || anomaliesData?.result_data?.anomalies || [];
    const summaryText = anomaliesData?.summary_text || anomaliesData?.result_text || '';

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('ai:summary.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label>{t('ai:summary.department')}:</label>
          <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
            <option value="">{t('ai:summary.allOrganization')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <button className="action-btn" onClick={generateAnomalies} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} /> {t('ai:anomalies.check')}
          </button>
        </div>

        {summaryText && (
          <div className="section-card">
            <h3><AlertTriangle size={18} /> {t('ai:anomalies.results')}</h3>
            <div className="summary-text">{summaryText}</div>
          </div>
        )}

        {anomalies.length > 0 ? (
          <div className="section-card">
            <h3>{t('ai:anomalies.detected', { count: anomalies.length })}</h3>
            <div className="anomalies-list">
              {anomalies.map((a, i) => (
                <div key={i} className={`anomaly-card ${a.severity || 'warning'}`}>
                  <div className="anomaly-header">
                    <AlertTriangle size={16} />
                    <strong>{a.metric_name || a.metric}</strong>
                    <span className={`severity-badge ${a.severity || 'warning'}`}>
                      {a.severity === 'critical' ? t('ai:anomalies.critical') : t('ai:anomalies.warning')}
                    </span>
                  </div>
                  <div className="anomaly-detail">
                    {a.department_name && <span>{t('ai:anomalies.department')}: {a.department_name}</span>}
                    <span>{t('ai:anomalies.value')}: {a.value}</span>
                    <span>{t('ai:anomalies.zScore')}: {(a.z_score || 0).toFixed(2)}</span>
                    {a.description && <p>{a.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          !loading && !summaryText && (
            <div className="no-data">{t('ai:anomalies.noData')}</div>
          )
        )}
      </div>
    );
  };

  // ─── Прогноз ──────────────────────────────────────────────────────────

  const generateForecast = async () => {
    setLoading(true);
    try {
      const data = { year, periods_ahead: 3 };
      if (departmentId) data.department_id = Number(departmentId);
      const res = await api.aiGenerateForecast(data);
      setForecastData(res.data);
    } catch (error) {
      console.error('Ошибка прогнозирования:', error);
    }
    setLoading(false);
  };

  const renderForecast = () => {
    const historical = forecastData?.historical || forecastData?.result_data?.historical || [];
    const forecast = forecastData?.forecast || forecastData?.result_data?.forecast || [];
    const summaryText = forecastData?.summary_text || forecastData?.result_text || '';
    const targets = forecastData?.targets || forecastData?.result_data?.targets || {};

    // Данные для графика
    const chartData = [
      ...historical.map(h => ({ period: h.period, value: h.value, type: 'history' })),
      ...forecast.map(f => ({ period: f.period, forecast: f.value, lower: f.lower, upper: f.upper })),
    ];

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('ai:summary.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label>{t('ai:summary.department')}:</label>
          <select value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
            <option value="">{t('ai:summary.allOrganization')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <button className="action-btn" onClick={generateForecast} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinner' : ''} /> {t('ai:forecast.generate')}
          </button>
        </div>

        {summaryText && (
          <div className="section-card">
            <h3><Target size={18} /> {t('ai:forecast.title')}</h3>
            <div className="summary-text">{summaryText}</div>
          </div>
        )}

        {chartData.length > 0 && (
          <div className="chart-card">
            <h3><Target size={18} /> {t('ai:forecast.historyAndForecast')}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="upper" stroke="none" fill="#7c3aed" fillOpacity={0.1} name={t('ai:forecast.upperBound')} />
                <Area type="monotone" dataKey="lower" stroke="none" fill="#7c3aed" fillOpacity={0.1} name={t('ai:forecast.lowerBound')} />
                <Line type="monotone" dataKey="value" stroke="#1e88e5" strokeWidth={2} dot name={t('ai:forecast.actual')} />
                <Line type="monotone" dataKey="forecast" stroke="#7c3aed" strokeWidth={2} strokeDasharray="5 5" dot name={t('ai:forecast.predicted')} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {Object.keys(targets).length > 0 && (
          <div className="section-card">
            <h3>{t('ai:forecast.targetValues')}</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('ai:forecast.metricCol')}</th>
                    <th>{t('ai:forecast.currentCol')}</th>
                    <th>{t('ai:forecast.targetCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(targets).map(([key, val]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{val.current}</td>
                      <td className="text-green">{val.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {chartData.length === 0 && !loading && !summaryText && (
          <div className="no-data">{t('ai:forecast.noData')}</div>
        )}
      </div>
    );
  };

  // ─── Сравнение ────────────────────────────────────────────────────────

  const loadComparison = async (mode) => {
    setLoading(true);
    try {
      if (mode === 'two' && compareDept1 && compareDept2) {
        const res = await api.aiCompare({
          year,
          department1_id: compareDept1,
          department2_id: compareDept2,
        });
        setCompareData(res.data);
      } else {
        const res = await api.aiCompare({ year });
        setCompareData(res.data);
      }
    } catch (error) {
      console.error('Ошибка сравнения:', error);
    }
    setLoading(false);
  };

  const renderCompare = () => {
    const ranking = compareData?.ranking || [];
    const comparison = compareData?.comparison || null;
    const radarData = comparison ? [
      { metric: t('ai:compare.reports'), dept1: comparison.dept1?.approval_rate || 0, dept2: comparison.dept2?.approval_rate || 0 },
      { metric: t('ai:compare.documents'), dept1: comparison.dept1?.on_time_rate || 0, dept2: comparison.dept2?.on_time_rate || 0 },
      { metric: t('ai:compare.plans'), dept1: comparison.dept1?.avg_plan_completion || 0, dept2: comparison.dept2?.avg_plan_completion || 0 },
      { metric: t('ai:compare.kpi'), dept1: comparison.dept1?.kpi_score || 0, dept2: comparison.dept2?.kpi_score || 0 },
    ] : [];

    return (
      <div className="tab-content">
        <div className="filter-bar">
          <label>{t('ai:summary.year')}:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="action-btn" onClick={() => loadComparison('all')}>
            <BarChart3 size={16} /> {t('ai:compare.rankAll')}
          </button>
        </div>

        <div className="filter-bar" style={{ marginTop: '-0.5rem' }}>
          <label>{t('ai:compare.compareTwoDepts')}:</label>
          <select value={compareDept1} onChange={e => setCompareDept1(e.target.value)}>
            <option value="">{t('ai:compare.dept1')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <span>vs</span>
          <select value={compareDept2} onChange={e => setCompareDept2(e.target.value)}>
            <option value="">{t('ai:compare.dept2')}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
            ))}
          </select>
          <button
            className="action-btn secondary"
            onClick={() => loadComparison('two')}
            disabled={!compareDept1 || !compareDept2}
          >
            <GitCompare size={16} /> {t('ai:compare.compareTwoDepts')}
          </button>
        </div>

        {/* Радар для двух отделов */}
        {comparison && radarData.length > 0 && (
          <div className="chart-card">
            <h3><GitCompare size={18} /> {comparison.dept1?.name} vs {comparison.dept2?.name}</h3>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis domain={[0, 100]} />
                <Radar name={comparison.dept1?.name} dataKey="dept1" stroke="#1e88e5" fill="#1e88e5" fillOpacity={0.3} />
                <Radar name={comparison.dept2?.name} dataKey="dept2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Рейтинг всех отделов */}
        {ranking.length > 0 && (
          <>
            <div className="chart-card">
              <h3><BarChart3 size={18} /> {t('ai:compare.kpiRating', { year })}</h3>
              <ResponsiveContainer width="100%" height={Math.max(300, ranking.length * 30)}>
                <BarChart data={ranking.slice(0, 15)} layout="vertical" margin={{ left: 180 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="department_name" width={170} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v} ${t('ai:compare.points')}`} />
                  <Bar dataKey="kpi_score" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="section-card">
              <h3>{t('ai:compare.fullRatingTable')}</h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('ai:summary.department')}</th>
                      <th>{t('ai:compare.kpi')}</th>
                      <th>{t('ai:compare.approvedReports')}</th>
                      <th>{t('ai:compare.onTimeRate')}</th>
                      <th>{t('ai:compare.plansCompleted')}</th>
                      <th>{t('ai:compare.overdue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((dept, i) => (
                      <tr key={dept.department_id || i}>
                        <td className="rank-cell">
                          <span className={`rank-badge ${i < 3 ? 'top' : ''}`}>{i + 1}</span>
                        </td>
                        <td>{dept.department_name}</td>
                        <td>
                          <div className="kpi-cell">
                            <div className="progress-bar-mini">
                              <div className="progress-fill" style={{
                                width: `${Math.min(dept.kpi_score || 0, 100)}%`,
                                background: (dept.kpi_score || 0) >= 70 ? '#10b981' : (dept.kpi_score || 0) >= 40 ? '#f59e0b' : '#ef4444'
                              }} />
                            </div>
                            <span>{dept.kpi_score || 0}</span>
                          </div>
                        </td>
                        <td>{dept.approval_rate || 0}%</td>
                        <td>{dept.on_time_rate || 0}%</td>
                        <td>{dept.avg_plan_completion || 0}%</td>
                        <td>
                          {(dept.overdue_tasks || 0) > 0
                            ? <span className="overdue-badge">{dept.overdue_tasks}</span>
                            : <span className="ok-badge">0</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {ranking.length === 0 && !comparison && !loading && (
          <div className="no-data">{t('ai:compare.selectMode')}</div>
        )}
      </div>
    );
  };

  // ─── Рекомендации ─────────────────────────────────────────────────────

  const generateRecs = async () => {
    setLoading(true);
    try {
      await api.aiGenerateRecommendations();
      const res = await api.aiGetRecommendations({ ordering: '-created_at' });
      setRecommendations(res.data?.results || res.data || []);
    } catch (error) {
      console.error('Ошибка генерации рекомендаций:', error);
    }
    setLoading(false);
  };

  const updateRecStatus = async (id, status) => {
    try {
      await api.aiUpdateRecommendation(id, { status });
      setRecommendations(prev => prev.map(r =>
        r.id === id ? { ...r, status } : r
      ));
    } catch (error) {
      console.error('Ошибка обновления:', error);
    }
  };

  const renderRecommendations = () => (
    <div className="tab-content">
      <div className="filter-bar">
        <button className="action-btn" onClick={generateRecs} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinner' : ''} /> {t('ai:recommendations.generate')}
        </button>
        <button className="action-btn secondary" onClick={() => loadTabData('recommendations')}>
          <RefreshCw size={16} /> {t('ai:recommendations.refreshList')}
        </button>
      </div>

      {recommendations.length > 0 ? (
        <div className="recs-grid">
          {recommendations.map(rec => (
            <div key={rec.id} className={`rec-card priority-${rec.priority}`}>
              <div className="rec-header">
                <span
                  className="priority-badge"
                  style={{ background: PRIORITY_COLORS[rec.priority] || '#94a3b8' }}
                >
                  {PRIORITY_LABELS[rec.priority] || rec.priority}
                </span>
                <span className="rec-type">{rec.recommendation_type_display || rec.recommendation_type}</span>
                <span className="rec-status">{STATUS_LABELS[rec.status] || rec.status}</span>
              </div>
              <h4 className="rec-title">{rec.title}</h4>
              <p className="rec-content">{rec.content}</p>
              {rec.department_name && (
                <div className="rec-dept">{t('ai:recommendations.department')}: {rec.department_name}</div>
              )}
              <div className="rec-actions">
                {rec.status === 'new' && (
                  <>
                    <button className="rec-btn accept" onClick={() => updateRecStatus(rec.id, 'accepted')}>
                      <Check size={14} /> {t('ai:recommendations.accept')}
                    </button>
                    <button className="rec-btn reject" onClick={() => updateRecStatus(rec.id, 'rejected')}>
                      <X size={14} /> {t('ai:recommendations.reject')}
                    </button>
                  </>
                )}
                {rec.status === 'accepted' && (
                  <button className="rec-btn done" onClick={() => updateRecStatus(rec.id, 'implemented')}>
                    <Check size={14} /> {t('ai:recommendations.markDone')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        !loading && <div className="no-data">{t('ai:recommendations.noRecommendations')}</div>
      )}
    </div>
  );

  // ─── Рендер активной вкладки ──────────────────────────────────────────

  const renderTabContent = () => {
    if (loading && activeTab !== 'chat') return <div className="loading"><Loader size={24} className="spinner" /> {t('common:loading')}</div>;
    switch (activeTab) {
      case 'chat':            return renderChat();
      case 'summary':         return renderSummary();
      case 'trends':          return renderTrends();
      case 'anomalies':       return renderAnomalies();
      case 'forecast':        return renderForecast();
      case 'compare':         return renderCompare();
      case 'recommendations': return renderRecommendations();
      default:                return null;
    }
  };

  return (
    <div className="ai-assistant">
      <div className="dashboard-header">
        <div>
          <h1><Brain size={28} /> {t('ai:title')}</h1>
          <p>{t('ai:subtitle')}</p>
        </div>
      </div>

      <div className="tab-nav">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {renderTabContent()}
    </div>
  );
}

export default AIAssistant;
